/** ADM-020: cases waiting for review, oldest first, with time against the 24-hour target. */
import * as stylex from "@stylexjs/stylex";
import type { AdminReviewQueue, AdminReviewQueueRow } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { api } from "./api.ts";
import { Person, RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { PageHead, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href } from "./router.ts";
import { ErrorText, Status } from "./ui.tsx";
import { refusal, waited } from "./words.ts";
const COLUMNS: readonly Column<AdminReviewQueueRow>[] = [{
  label: "Patient",
  cell: row => <Person name={row.patientName ?? "Name not given"} to={href.review(row.id)} />
}, {
  label: "Record",
  cell: row => <RecordLink to={href.patient(row.patientId)} relation="Patient" label={row.patientName ?? "Record"} />
}, {
  label: "Status",
  nowrap: true,
  cell: row => row.status === "in_progress" ? <Status tone="warn">In progress</Status> : <Status tone="neutral">Waiting</Status>
}, {
  label: "Waiting",
  numeric: true,
  phoneLabel: true,
  cell: row => row.pastTarget ? <Status tone="danger">{waited(row.waitingMs)}</Status> : waited(row.waitingMs)
}, {
  label: "Formula version",
  phoneLabel: true,
  cell: row => <span className="x1jty25b">
        <Truncate value={row.formulaVersionId} />
      </span>
}];
function Queue({
  queue
}: {
  readonly queue: AdminReviewQueue;
}) {
  const meta = queue.reviews.length === 0 ? "No cases waiting." : queue.pastTarget === 0 ? `${queue.reviews.length} waiting, none past 24 hours` : `${queue.reviews.length} waiting, ${queue.pastTarget} past 24 hours`;
  return <>
      <PageHead title="Reviews" meta={meta} />
      <div className="x78zum5 xdt5ytf xd3upel">
        <Section title="Queue" count={queue.reviews.length}>
          <Table label="Review queue" columns={COLUMNS} rows={queue.reviews} rowKey={row => row.id} empty="No cases waiting for review." />
        </Section>
      </div>
    </>;
}
export function ReviewQueue() {
  const [loaded] = useLoad("reviews", () => api<AdminReviewQueue>(ROUTES.adminReviews));
  if (loaded.state === "loading") return <PageHead title="Reviews" meta="Loading" />;
  if (loaded.state === "failed") return <>
        <PageHead title="Reviews" />
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </>;
  return <Queue queue={loaded.data} />;
}